import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Plus, Pencil, Trash2, Package, Eye, Settings } from 'lucide-react';
import { productsAPI } from '../services/api';
import { getImageUrl, formatPrice } from '../utils/helpers';
import { toast } from '../hooks/use-toast';
import ProductWizard from '../components/ProductWizard';

const SellerProducts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const loadProducts = useCallback(async () => {
    if (!user?._id) return;
    try {
      const data = await productsAPI.getBySeller(user._id, true);
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  }, [user?._id]);

  useEffect(() => {
    if (user) loadProducts();
  }, [user, loadProducts]);

  const hasStoreConfig = user?.store_config?.store_name;

  const toggleProductActive = async (productId) => {
    try {
      await productsAPI.toggleActive(productId);
      await loadProducts();
      toast({ title: 'Estado actualizado' });
    } catch {
      toast({ title: 'Error', description: 'No se pudo cambiar el estado', variant: 'destructive' });
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await productsAPI.delete(id);
      await loadProducts();
      toast({ title: 'Producto eliminado' });
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowWizard(true);
  };

  const handleWizardClose = () => {
    setShowWizard(false);
    setEditingProduct(null);
  };

  const handleWizardSuccess = async () => {
    setShowWizard(false);
    setEditingProduct(null);
    await loadProducts();
  };

  if (!user) return null;

  // Wizard overlay
  if (showWizard) {
    return (
      <ProductWizard
        onClose={handleWizardClose}
        onSuccess={handleWizardSuccess}
        editingProduct={editingProduct}
        user={user}
      />
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Mis Publicaciones</h1>
            <p className="text-gray-600 mt-1">{products.length} productos publicados</p>
          </div>
          <div className="hidden sm:flex space-x-3">
            <Button onClick={() => navigate('/store-config')} variant="outline">
              <Settings className="w-5 h-5 mr-2" /> Configurar Tienda
            </Button>
            <Button onClick={() => setShowWizard(true)} className="bg-green-600 hover:bg-green-700" disabled={!hasStoreConfig}
              data-testid="publish-product-btn">
              <Plus className="w-5 h-5 mr-2" /> Publicar producto
            </Button>
          </div>
        </div>

        {/* Mobile top button */}
        <Button onClick={() => setShowWizard(true)} disabled={!hasStoreConfig}
          className="sm:hidden w-full mb-4 bg-green-600 hover:bg-green-700 h-12 text-base sticky top-16 z-30"
          data-testid="publish-product-btn-mobile">
          <Plus className="w-5 h-5 mr-2" /> Publicar Nuevo Producto
        </Button>

        {/* Store Config Warning */}
        {!hasStoreConfig && (
          <Card className="p-5 mb-6 bg-yellow-50 border-yellow-200">
            <h3 className="font-semibold text-yellow-900 mb-1">Configura tu tienda primero</h3>
            <p className="text-yellow-700 text-sm mb-3">Debes configurar tu tienda antes de publicar productos</p>
            <Button onClick={() => navigate('/store-config')} size="sm" className="bg-yellow-600 hover:bg-yellow-700">
              <Settings className="w-4 h-4 mr-2" /> Configurar Ahora
            </Button>
          </Card>
        )}

        {/* Products List */}
        {products.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="w-24 h-24 mx-auto text-gray-300 mb-4" />
            <h2 className="text-2xl font-bold mb-2">No tienes productos publicados</h2>
            <p className="text-gray-600 mb-6">
              {hasStoreConfig ? 'Comienza a vender publicando tu primer producto' : 'Configura tu tienda para empezar'}
            </p>
            {hasStoreConfig ? (
              <Button onClick={() => setShowWizard(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-5 h-5 mr-2" /> Publicar mi primer producto
              </Button>
            ) : (
              <Button onClick={() => navigate('/store-config')} className="bg-zl-orange hover:bg-zl-orange-600">
                <Settings className="w-5 h-5 mr-2" /> Configurar Tienda
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {products.map(product => (
              <Card key={product._id} className="p-4 sm:p-6" data-testid={`product-card-${product._id}`}>
                <div className="flex gap-4 sm:gap-6">
                  <img
                    src={getImageUrl(product.images?.[0])}
                    alt={product.title}
                    className="w-20 h-20 sm:w-32 sm:h-32 object-cover rounded flex-shrink-0"
                    onError={e => { e.target.src = 'https://via.placeholder.com/128?text=No+img'; }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1 sm:mb-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-base sm:text-xl font-semibold truncate">{product.title}</h3>
                          <Badge variant={product.active ? "default" : "secondary"} className="text-xs shrink-0">
                            {product.active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                        <Badge variant="outline" className="text-xs">{product.category}</Badge>
                      </div>
                      <p className="text-lg sm:text-2xl font-bold text-zl-orange whitespace-nowrap">
                        {formatPrice(product.price)}
                      </p>
                    </div>

                    <p className="text-gray-600 text-sm line-clamp-1 sm:line-clamp-2 mb-2 sm:mb-3 hidden sm:block">{product.description}</p>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="text-xs sm:text-sm text-gray-600">
                        <span className="font-semibold">Stock:</span> {product.stock}
                        {product.shipping_info && (
                          <span className="ml-3">
                            <span className="font-semibold">Envío:</span>{' '}
                            {product.shipping_info.shipping_cost === 0 ? 'Gratis' : formatPrice(product.shipping_info.shipping_cost)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor={`active-${product._id}`} className="text-xs">{product.active ? 'Activo' : 'Inactivo'}</Label>
                          <Switch id={`active-${product._id}`} checked={product.active}
                            onCheckedChange={() => toggleProductActive(product._id)} />
                        </div>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/product/${product._id}`)} className="h-8 px-2 sm:px-3">
                          <Eye className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Ver</span>
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleEdit(product)} className="h-8 px-2 sm:px-3">
                          <Pencil className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Editar</span>
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 px-2 sm:px-3 text-red-600 hover:bg-red-50"
                          onClick={() => deleteProduct(product._id)}>
                          <Trash2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Eliminar</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

      {/* FAB for mobile (when products exist) */}
      {products.length > 0 && hasStoreConfig && (
        <button
          onClick={() => setShowWizard(true)}
          className="sm:hidden fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-green-600 text-white shadow-lg flex items-center justify-center hover:bg-green-700 active:scale-95 transition-transform"
          data-testid="fab-publish"
        >
          <Plus className="w-7 h-7" />
        </button>
      )}
    </div>
  );
};

export default SellerProducts;
